// Renders the artwork template preview to an off-screen canvas and returns a PNG blob.
import {
  TEMPLATE_DESIGNS,
  TEMPLATE_FORMATS,
  TEMPLATE_MODE_PALETTE,
} from "../constants";

const FONT_FAMILY = '"Satoshi", "Inter", "Segoe UI", sans-serif';

const OVERLAY_COLOR = {
  dark: "rgba(0, 0, 0, 0.35)",
  light: "rgba(248, 248, 252, 0.45)",
};

function loadImage(src) {
  if (!src) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Template image could not be loaded"));
    image.src = src;
  });
}

function scaleToCover(image, targetWidth, targetHeight) {
  const scale = Math.max(targetWidth / image.width, targetHeight / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  const x = (targetWidth - width) / 2;
  const y = (targetHeight - height) / 2;

  return { width, height, x, y };
}

function scaleToFit(image, targetWidth, targetHeight) {
  const scale = Math.min(targetWidth / image.width, targetHeight / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  const x = (targetWidth - width) / 2;
  const y = (targetHeight - height) / 2;

  return { width, height, x, y };
}

function wrapTextLines(context, text, maxWidth) {
  if (!text) {
    return [];
  }

  const words = text.trim().split(/\s+/);
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const { width } = context.measureText(testLine);

    if (width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

function drawTextBlock(context, {
  text,
  top,
  maxWidth,
  font,
  lineHeight,
  color,
  x,
}) {
  context.font = font;
  context.fillStyle = color;
  context.textAlign = "center";
  context.textBaseline = "top";

  const lines = wrapTextLines(context, text, maxWidth);
  lines.forEach((line, index) => {
    context.fillText(line, x, top + index * lineHeight);
  });

  return top + lines.length * lineHeight;
}

// Layout for template design I – text header and cropped artwork.
function drawTypeOneTemplate(context, options) {
  const {
    width,
    height,
    palette,
    artistName,
    artworkTitle,
    image,
  } = options;

  context.fillStyle = palette.background;
  context.fillRect(0, 0, width, height);

  const headerHeight = Math.round(height * 0.22);
  const headerPadding = Math.round(headerHeight * 0.16);
  const contentWidth = width - headerPadding * 2;
  const centerX = width / 2;

  const artistFontSize = Math.round(height * 0.036);
  const titleFontSize = Math.round(height * 0.024);
  const ctaFontSize = Math.round(height * 0.022);

  let cursorY = headerPadding;

  cursorY = drawTextBlock(context, {
    text: artistName,
    top: cursorY,
    maxWidth: contentWidth,
    font: `600 ${artistFontSize}px ${FONT_FAMILY}`,
    lineHeight: artistFontSize * 1.15,
    color: palette.contrast,
    x: centerX,
  });

  cursorY += Math.round(height * 0.012);

  cursorY = drawTextBlock(context, {
    text: artworkTitle,
    top: cursorY,
    maxWidth: contentWidth,
    font: `italic 500 ${titleFontSize}px ${FONT_FAMILY}`,
    lineHeight: titleFontSize * 1.15,
    color: palette.contrast,
    x: centerX,
  });

  cursorY += Math.round(height * 0.016);

  drawTextBlock(context, {
    text: "See full at Suarte",
    top: cursorY,
    maxWidth: contentWidth,
    font: `500 ${ctaFontSize}px ${FONT_FAMILY}`,
    lineHeight: ctaFontSize * 1.1,
    color: palette.contrast,
    x: centerX,
  });

  if (!image) {
    return;
  }

  const availableHeight = height - headerHeight;
  const { width: drawWidth, height: drawHeight, x, y } = scaleToCover(
    image,
    width,
    availableHeight,
  );

  context.drawImage(image, x, headerHeight + y, drawWidth, drawHeight);
}

// Layout for template design II – blurred background with centered artwork.
function drawTypeTwoTemplate(context, options) {
  const {
    width,
    height,
    palette,
    artistName,
    artworkTitle,
    image,
    mode,
  } = options;

  context.fillStyle = palette.background;
  context.fillRect(0, 0, width, height);

  if (image) {
    context.save();
    context.filter = "blur(48px)";
    const { width: drawWidth, height: drawHeight, x, y } = scaleToCover(
      image,
      width * 1.1,
      height * 1.1,
    );
    context.drawImage(image, x - (drawWidth - width) / 2, y - (drawHeight - height) / 2, drawWidth, drawHeight);
    context.restore();
  }

  context.fillStyle = OVERLAY_COLOR[mode] ?? OVERLAY_COLOR.dark;
  context.fillRect(0, 0, width, height);

  const centerX = width / 2;
  const contentWidth = Math.min(width * 0.72, 760);
  let cursorY = height * 0.18;

  const headingFont = Math.round(height * 0.034);
  const titleFont = Math.round(height * 0.026);
  const ctaFont = Math.round(height * 0.023);

  cursorY = drawTextBlock(context, {
    text: artistName,
    top: cursorY,
    maxWidth: contentWidth,
    font: `600 ${headingFont}px ${FONT_FAMILY}`,
    lineHeight: headingFont * 1.12,
    color: palette.contrast,
    x: centerX,
  });

  cursorY += height * 0.02;

  cursorY = drawTextBlock(context, {
    text: artworkTitle,
    top: cursorY,
    maxWidth: contentWidth,
    font: `italic 500 ${titleFont}px ${FONT_FAMILY}`,
    lineHeight: titleFont * 1.15,
    color: palette.contrast,
    x: centerX,
  });

  cursorY += height * 0.04;

  if (image) {
    const maxImageWidth = contentWidth * 0.9;
    const maxImageHeight = height * 0.42;
    const { width: drawWidth, height: drawHeight } = scaleToFit(
      image,
      maxImageWidth,
      maxImageHeight,
    );
    const imageX = centerX - drawWidth / 2;

    context.save();
    context.shadowColor = "rgba(0, 0, 0, 0.35)";
    context.shadowBlur = height * 0.035;
    context.drawImage(image, imageX, cursorY, drawWidth, drawHeight);
    context.restore();

    cursorY += drawHeight + height * 0.04;
  }

  drawTextBlock(context, {
    text: "Available at Suarte",
    top: cursorY,
    maxWidth: contentWidth,
    font: `500 ${ctaFont}px ${FONT_FAMILY}`,
    lineHeight: ctaFont * 1.12,
    color: palette.contrast,
    x: centerX,
  });
}

// Layout for template design III – minimal centered artwork on solid background.
function drawTypeThreeTemplate(context, options) {
  const {
    width,
    height,
    palette,
    artistName,
    artworkTitle,
    image,
  } = options;

  context.fillStyle = palette.background;
  context.fillRect(0, 0, width, height);

  const centerX = width / 2;
  const contentWidth = Math.min(width * 0.74, 760);
  let cursorY = height * 0.18;

  const headingFont = Math.round(height * 0.032);
  const titleFont = Math.round(height * 0.026);
  const ctaFont = Math.round(height * 0.023);

  cursorY = drawTextBlock(context, {
    text: artistName,
    top: cursorY,
    maxWidth: contentWidth,
    font: `600 ${headingFont}px ${FONT_FAMILY}`,
    lineHeight: headingFont * 1.12,
    color: palette.contrast,
    x: centerX,
  });

  cursorY += height * 0.018;

  cursorY = drawTextBlock(context, {
    text: artworkTitle,
    top: cursorY,
    maxWidth: contentWidth,
    font: `italic 500 ${titleFont}px ${FONT_FAMILY}`,
    lineHeight: titleFont * 1.15,
    color: palette.contrast,
    x: centerX,
  });

  cursorY += height * 0.045;

  if (image) {
    const maxImageWidth = contentWidth * 0.9;
    const maxImageHeight = height * 0.46;
    const { width: drawWidth, height: drawHeight } = scaleToFit(
      image,
      maxImageWidth,
      maxImageHeight,
    );
    const imageX = centerX - drawWidth / 2;

    context.save();
    context.shadowColor = "rgba(0, 0, 0, 0.28)";
    context.shadowBlur = height * 0.03;
    context.drawImage(image, imageX, cursorY, drawWidth, drawHeight);
    context.restore();

    cursorY += drawHeight + height * 0.04;
  }

  drawTextBlock(context, {
    text: "Available at Suarte",
    top: cursorY,
    maxWidth: contentWidth,
    font: `500 ${ctaFont}px ${FONT_FAMILY}`,
    lineHeight: ctaFont * 1.12,
    color: palette.contrast,
    x: centerX,
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }

      reject(new Error("Unable to render template"));
    }, "image/png");
  });
}

async function prepareImageSources({
  preferred,
  fallback,
}) {
  if (preferred) {
    try {
      const image = await loadImage(preferred);
      if (image) {
        return image;
      }
    } catch (error) {
      console.warn("Preferred template image failed to load", error);
    }
  }

  if (fallback) {
    try {
      const image = await loadImage(fallback);
      if (image) {
        return image;
      }
    } catch (error) {
      console.warn("Fallback template image failed to load", error);
    }
  }

  return null;
}

export default async function renderTemplateToBlob({
  design,
  format,
  mode = "dark",
  artistName,
  artworkTitle,
  preferredImage,
  fallbackImage,
}) {
  if (!design || !format) {
    throw new Error("Missing template configuration");
  }

  const palette = TEMPLATE_MODE_PALETTE[mode] || TEMPLATE_MODE_PALETTE.dark;
  const width = 1080;
  const height = format === TEMPLATE_FORMATS.POST ? 1350 : 1920;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  const image = await prepareImageSources({
    preferred: preferredImage,
    fallback: fallbackImage,
  });

  const drawOptions = {
    width,
    height,
    palette,
    artistName,
    artworkTitle,
    image,
    mode,
  };

  switch (design) {
  case TEMPLATE_DESIGNS.TYPE_ONE:
    drawTypeOneTemplate(context, drawOptions);
    break;
  case TEMPLATE_DESIGNS.TYPE_TWO:
    drawTypeTwoTemplate(context, drawOptions);
    break;
  case TEMPLATE_DESIGNS.TYPE_THREE:
    drawTypeThreeTemplate(context, drawOptions);
    break;
  default:
    throw new Error("Unknown template design");
  }

  return canvasToBlob(canvas);
}
